// ============ User Preferences ============

export interface UserPreferences {
  id: string;
  userId: string;
  theme: ThemeMode;
  language: string;
  timezone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  notifications: NotificationPreferences;
  dashboard: DashboardPreferences;
  createdAt: string;
  updatedAt: string;
}

export type ThemeMode = 'light' | 'dark' | 'system';

export interface NotificationPreferences {
  email: {
    enabled: boolean;
    campaignUpdates: boolean;
    billingAlerts: boolean;
    securityAlerts: boolean;
    weeklyReports: boolean;
    marketingEmails: boolean;
  };
  push: {
    enabled: boolean;
    callEvents: boolean;
    messageEvents: boolean;
    campaignEvents: boolean;
    billingAlerts: boolean;
    systemAlerts: boolean;
  };
  inApp: {
    enabled: boolean;
    showBadge: boolean;
    playSound: boolean;
  };
}

export interface DashboardPreferences {
  defaultView: 'overview' | 'telephony' | 'whatsapp' | 'sms' | 'rcs';
  widgetsOrder: string[];
  hiddenWidgets: string[];
  refreshInterval: number; // seconds, 0 = manual
  compactMode: boolean;
}

// ============ Security Settings ============

export interface SecuritySettings {
  twoFactorEnabled: boolean;
  twoFactorMethod?: '2fa_app' | 'sms' | 'email';
  sessionTimeout: number; // minutes
  ipWhitelist: string[];
  loginNotifications: boolean;
  lastPasswordChange?: string;
  activeSessions: ActiveSession[];
}

export interface ActiveSession {
  id: string;
  deviceName: string;
  deviceInfo: string;
  deviceType?: 'desktop' | 'mobile' | 'tablet'; // Optional for backward compatibility
  browser?: string; // Optional, can be parsed from deviceInfo
  os?: string; // Optional, can be parsed from deviceInfo
  ipAddress: string;
  location?: string;
  lastAccessedAt: string; // Changed from lastActive to match backend
  createdAt: string;
  expiresAt: string;
  current: boolean; // Changed from isCurrent to match backend
}

export interface PasswordChangeRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface TwoFactorSetupResponse {
  secret: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

// ============ Notifications ============

export enum NotificationType {
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  ALERT = 'ALERT'
}

export enum NotificationCategory {
  CALL = 'CALL',
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
  RCS = 'RCS',
  CAMPAIGN = 'CAMPAIGN',
  BILLING = 'BILLING',
  SECURITY = 'SECURITY',
  SYSTEM = 'SYSTEM'
}

export interface NotificationDTO {
  id: string;
  userId: string;
  organizationId?: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  data?: Record<string, any>;
  link?: string;
  read: boolean;
  readAt?: string;
  createdAt: string;
}

export interface NotificationStats {
  total: number;
  unread: number;
  byCategory: Record<NotificationCategory, number>;
}

// ============ Analytics ============

export interface AnalyticsOverview {
  period: { start: string; end: string };
  totalCalls: number;
  totalMessages: number;
  totalCampaigns: number;
  totalRevenue: number;
  callsChange: number; // percentage
  messagesChange: number;
  campaignsChange: number;
  revenueChange: number;
}

export interface ChannelAnalytics {
  channel: 'voice' | 'sms' | 'whatsapp' | 'rcs';
  totalVolume: number;
  successRate: number;
  avgResponseTime?: number;
  cost: number;
  revenue: number;
  trend: TrendData[];
}

export interface TrendData {
  date: string;
  value: number;
  label?: string;
}

export interface CampaignAnalytics {
  totalCampaigns: number;
  activeCampaigns: number;
  completedCampaigns: number;
  successRate: number;
  totalReach: number;
  engagementRate: number;
  conversionRate: number;
  byChannel: Record<string, number>;
  topPerforming: Array<{
    id: string;
    name: string;
    channel: string;
    successRate: number;
    reach: number;
  }>;
}

export interface GeographicAnalytics {
  country: string;
  region?: string;
  calls: number;
  messages: number;
  revenue: number;
}

export interface TimeAnalytics {
  hourlyDistribution: Array<{ hour: number; volume: number }>;
  dailyDistribution: Array<{ day: string; volume: number }>;
  peakHours: number[];
  peakDays: string[];
}

// ============ Help/Support ============

export enum TicketStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING_RESPONSE = 'WAITING_RESPONSE',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED'
}

export enum TicketPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT'
}

export enum TicketCategory {
  BILLING = 'BILLING',
  TECHNICAL = 'TECHNICAL',
  FEATURE_REQUEST = 'FEATURE_REQUEST',
  BUG_REPORT = 'BUG_REPORT',
  ACCOUNT = 'ACCOUNT',
  OTHER = 'OTHER'
}

export interface SupportTicket {
  id: string;
  organizationId: string;
  userId: string;
  userName: string;
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  assignedTo?: string;
  assignedToName?: string;
  attachments?: string[];
  messages: TicketMessage[];
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

export interface TicketMessage {
  id: string;
  ticketId: string;
  userId: string;
  userName: string;
  isStaff: boolean;
  message: string;
  attachments?: string[];
  createdAt: string;
}

export interface CreateTicketRequest {
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  attachments?: File[];
}

export interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  helpful: number;
  notHelpful: number;
  order: number;
}

export interface FAQCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  itemCount: number;
}
